import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const handleCategoryClick = (url: string) => {
    window.open(url, "_blank", "noopener,nofollow");
    setMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-14 md:h-18">
            {/* Logo — shortened on mobile */}
            <Link
              to="/"
              onClick={handleNavClick}
              className="text-base md:text-xl font-heading font-bold text-foreground-950 tracking-tight whitespace-nowrap flex-shrink-0"
            >
              <span className="md:hidden">Kakobuy</span>
              <span className="hidden md:inline">Kakobuy</span>
              <span className="text-primary-500">Spreadsheet</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                onClick={handleNavClick}
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("nav_home")}
              </Link>
              <Link
                to="/artykul/kakobuy-spreadsheet-przewodnik"
                onClick={handleNavClick}
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("nav_shopping_guide")}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {t("nav_spreadsheet")}
              </a>
              <Link
                to="/artykul/kakobuy-spreadsheet-przewodnik"
                onClick={handleNavClick}
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("nav_guides")}
              </Link>
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("hero_cta_primary")}
              </a>
            </div>

            {/* Mobile: hamburger only — clean and never overflows */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-200/60 transition-colors cursor-pointer flex-shrink-0"
                aria-label={mobileMenuOpen ? t("nav_mobile_close") : t("nav_mobile_menu")}
              >
                {mobileMenuOpen ? (
                  <i className="ri-close-line text-xl text-foreground-900"></i>
                ) : (
                  <i className="ri-menu-line text-xl text-foreground-900"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          {/* Menu Panel — slides in from right */}
          <div className="absolute top-0 right-0 w-[85%] max-w-[360px] h-full bg-background-50 shadow-lg animate-slide-in flex flex-col">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-5 h-14 flex-shrink-0 border-b border-background-200/50">
              <span className="text-sm font-heading font-bold text-foreground-950">
                Kakobuy<span className="text-primary-500">Spreadsheet</span>
              </span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg text-foreground-700"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
              {/* CTA Button at top */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 mb-5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line text-sm"></i>
                {t("hero_cta_primary")}
              </a>

              {/* Primary Links */}
              <div className="space-y-1 mb-6">
                <Link
                  to="/"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-home-4-line text-primary-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{t("nav_home")}</span>
                </Link>

                <Link
                  to="/artykul/kakobuy-spreadsheet-przewodnik"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-book-open-line text-accent-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{t("nav_shopping_guide")}</span>
                </Link>

                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                    <i className="ri-external-link-line text-secondary-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{t("nav_spreadsheet")}</span>
                </a>
              </div>

              {/* Categories Divider */}
              <div className="mb-3">
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3">
                  {t("nav_categories")}
                </span>
              </div>

              {/* All Categories */}
              <div className="space-y-0.5 mb-6">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 flex-shrink-0">
                      <i className="ri-arrow-right-up-line text-foreground-500 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-800 truncate">{t(cat.nameKey)}</span>
                  </button>
                ))}
              </div>

              {/* Bottom Links */}
              <div className="border-t border-background-200/70 pt-4 space-y-1">
                <Link
                  to="/artykul/kakobuy-spreadsheet-przewodnik"
                  onClick={handleNavClick}
                  className="block py-2 px-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  {t("nav_guides")}
                </Link>
                <Link
                  to="/artykul/kakobuy-bezpieczenstwo-zakupow"
                  onClick={handleNavClick}
                  className="block py-2 px-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  {t("nav_safety")}
                </Link>
                <Link
                  to="/artykul/kakobuy-wysylka-polska"
                  onClick={handleNavClick}
                  className="block py-2 px-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  Wysyłka do Polski
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.25s ease-out;
        }
      `}</style>
    </>
  );
}